// What each plan includes, in WORDS — derived from PLANS, never typed out.
//
// Why this exists: the homepage FAQ told visitors the Checkpoint was something
// "Pro unlocks", while Pricing (and the start route) gave Free one a month. Each
// surface had its own hand-written sentence, and they drifted. Marketing, help
// and access screens build their plan sentences from these instead, so a
// change to the allowance in plan-config.ts changes the copy everywhere.
//
// The real entitlement (src/lib/auth/plan-guard.ts checkCheckpointLimit +
// /api/sat/start): Free may finish PLANS.FREE.checkpointsPerMonth full
// Checkpoints per calendar month (the user's local month); a single-section
// Checkpoint is Pro-only; Pro is unlimited.

import { PLANS, TUTOR_DAILY_CAP, type PlanKey } from './plan-config'
import { countPhrase } from './count-phrase'

/** "a month" for a finite monthly allowance; nothing for an unlimited one. */
function perMonth(n: number): string {
  return n === Infinity ? '' : ' a month'
}

/** e.g. "one Checkpoint a month" (Free) / "unlimited Checkpoints" (Pro). */
export function checkpointAllowance(plan: PlanKey): string {
  const n = PLANS[plan].checkpointsPerMonth
  return `${countPhrase(n, 'Checkpoint')}${perMonth(n)}`
}

/** e.g. "one full-length SAT a month" (Free) / "unlimited full-length SATs" (Pro). */
export function fullLengthAllowance(plan: PlanKey): string {
  const n = PLANS[plan].testsPerMonth
  return `${countPhrase(n, 'full-length SAT')}${perMonth(n)}`
}

/**
 * Both measurement allowances in one phrase, e.g. "one full-length SAT and one
 * Checkpoint a month". Shares the trailing "a month" when both are monthly.
 */
export function testAllowances(plan: PlanKey): string {
  const tests = PLANS[plan].testsPerMonth
  const checkpoints = PLANS[plan].checkpointsPerMonth
  if (tests !== Infinity && checkpoints !== Infinity) {
    return `${countPhrase(tests, 'full-length SAT')} and ${countPhrase(checkpoints, 'Checkpoint')} a month`
  }
  if (tests === Infinity && checkpoints === Infinity) {
    return 'unlimited full-length SATs and Checkpoints'
  }
  return `${fullLengthAllowance(plan)} and ${checkpointAllowance(plan)}`
}

/** Free's allowances, e.g. "one full-length SAT and one Checkpoint a month". */
export const FREE_TEST_ALLOWANCES = testAllowances('FREE')

/** Pro's allowances, e.g. "unlimited full-length SATs and Checkpoints". */
export const PRO_TEST_ALLOWANCES = testAllowances('PRO')

/** Free's Checkpoint allowance alone, e.g. "one Checkpoint a month". */
export const FREE_CHECKPOINT_ALLOWANCE = checkpointAllowance('FREE')

/** Capitalise the first letter — for an allowance that starts a sentence. */
export function sentenceCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Sage on each plan, e.g. "3 Sage messages a day" (Free) / "50 Sage messages a
 * day" (Pro). Sage isn't Pro-only — Free gets a daily taste — so copy must never
 * say Pro "unlocks" it.
 */
export function sageAllowance(plan: PlanKey): string {
  return `${TUTOR_DAILY_CAP[plan]} Sage messages a day`
}
